import React from "react";
import { toTitleCase } from "shared/@utils/formatString";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

import OpenSansLight from "./pdfFonts/OpenSans-Light.ttf";
import OpenSansRegular from "./pdfFonts/OpenSans-Regular.ttf";

Font.register({
  family: "Open Sans",
  fonts: [
    {
      src: OpenSansRegular,
    },
    {
      src: OpenSansLight,
      fontWeight: "light",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    backgroundColor: "#FFFFFF",
    fontFamily: "Open Sans",
    fontWeight: "light",
    padding: "36px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    fontSize: "15px",
    width: "100%",
  },
  row: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  date: {
    color: "#243746",
    fontWeight: "bold",
  },
  title: {
    color: "#415364",
    marginTop: "12px",
    fontSize: "24px",
  },
  attorneyNotice: {
    color: "#999999",
  },
  light: {
    fontWeight: "light",
  },
  settingsItem: {
    width: "100%",
    position: "relative",
    display: "flex",
    flexDirection: "row",
    minHeight: "12px",
    maxHeight: "100%",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: "3px",
    marginBottom: "3px",
    fontSize: "8px",
    wordWrap: "normal",
  },
  footer: {
    position: "absolute",
    fontSize: "8px",
    bottom: -10,
    width: "100%",
    display: "flex",
    maxHeight: "8px",
    textAlign: "center",
  },
  body: {
    width: "100%",
    flex: 1,
    position: "relative",
  },
  diamondName: {
    fontWeight: "normal",
  },
});

const todaysDate = dayjs().format("MMMM DD, YYYY");

export function SettingsPdf({
  date,
  diamondSettings,
  pdfColumnSelect,
  pdfSortSelect,
}) {
  const sort = pdfSortSelect;
  const cols = pdfColumnSelect;
  const _diamondSettings = diamondSettings.sort((a, b) => {
    if (sort === "Diamond Name") {
      return b.profile.lastName - a.profile.lastName;
    }
    if (sort === "GAR Points") {
      return (
        b.businessInfo.qualifyingRequirementsByYear[0].garPoints -
        a.businessInfo.qualifyingRequirementsByYear[0].garPoints
      );
    }
    if (sort === "Integrated GAR") {
      return (
        b.businessInfo.qualifyingRequirementsByYear[0].integratedGarPoints -
        a.businessInfo.qualifyingRequirementsByYear[0].integratedGarPoints
      );
    }
    if (sort === "Voting Member") {
      return b.businessInfo.qualifyingRequirementsByYear[0].isVotingMember ===
        a.businessInfo.qualifyingRequirementsByYear[0].isVotingMember
        ? 0
        : b.businessInfo.qualifyingRequirementsByYear[0].isVotingMember
        ? 1
        : -1;
    }
    if (sort === "Capital Share") {
      return (
        b.businessInfo.qualifyingRequirementsByYear[0].capitalShare -
        a.businessInfo.qualifyingRequirementsByYear[0].capitalShare
      );
    }
    if (sort === "Year Qualified") {
      return (
        b.businessInfo.yearDiamondQualified -
        a.businessInfo.yearDiamondQualified
      );
    }
  });

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.row}>
            <Text style={styles.date}>{date}</Text>
            <Text style={styles.attorneyNotice}>
              Confidential and Subject to Attorney Client Privilege
            </Text>
          </View>
          <View style={styles.row}>
            <Text style={styles.title}>Diamond Settings</Text>
          </View>
          <View
            style={[
              styles.row,
              {
                borderBottom: "1px solid #415364",
                height: "1px",
                marginTop: "10px",
              },
            ]}
          />
        </View>
        <View style={[styles.body, { marginTop: "10px" }]}>
          <View>
            <View style={styles.settingsItem} wrap={false}>
              {(cols.includes("Name") || cols.length < 1) && (
                <Text style={[styles.light, { width: "20%" }]}>Diamond</Text>
              )}
              {(cols.includes("GAR") || cols.length < 1) && (
                <Text style={[styles.light, { width: "8%" }]}>Gar Points</Text>
              )}
              {(cols.includes("Integrated GAR") || cols.length < 1) && (
                <Text style={[styles.light, { width: "11%" }]}>
                  Integrated Gar
                </Text>
              )}
              {(cols.includes("Pin Wearing") || cols.length < 1) && (
                <Text style={[styles.light, { width: "22%" }]}>
                  Pin Wearing
                </Text>
              )}
              {(cols.includes("Executive Committee") || cols.length < 1) && (
                <Text style={[styles.light, { width: "10%" }]}>
                  Executive Committee
                </Text>
              )}
              {(cols.includes("Voting Member") || cols.length < 1) && (
                <Text style={[styles.light, { width: "10%" }]}>
                  {`Voting\nMember`}
                </Text>
              )}
              {(cols.includes("Capital Share") || cols.length < 1) && (
                <Text style={[styles.light, { width: "10%" }]}>
                  Capital Share
                </Text>
              )}
              {(cols.includes("Year Qualified") || cols.length < 1) && (
                <Text style={[styles.light, { width: "10%" }]}>
                  Year Qualified
                </Text>
              )}
            </View>
            {_diamondSettings.map((diamond, index) => {
              const currentInfo = diamond.businessInfo
                .qualifyingRequirementsByYear
                ? diamond.businessInfo.qualifyingRequirementsByYear.filter(
                    (info) => parseInt(info.year) === parseInt(date)
                  )[0]
                : null;
              return (
                <View key={index} style={styles.settingsItem} wrap={false}>
                  {(cols.includes("Name") || cols.length < 1) && (
                    <Text style={[styles.diamondName, { width: "20%" }]}>
                      {toTitleCase(
                        `${diamond.profile.firstName} ${diamond.profile.lastName}`
                      )}
                    </Text>
                  )}
                  {(cols.includes("GAR") || cols.length < 1) && (
                    <Text style={[styles.light, { width: "8%" }]}>
                      {currentInfo ? currentInfo.garPoints : ""}
                    </Text>
                  )}
                  {(cols.includes("Integrated GAR") || cols.length < 1) && (
                    <Text style={[styles.light, { width: "11%" }]}>
                      {currentInfo ? currentInfo.integratedGarPoints : ""}
                    </Text>
                  )}
                  {(cols.includes("Pin Wearing") || cols.length < 1) && (
                    <Text style={[styles.light, { width: "22%" }]}>
                      {currentInfo ? currentInfo.pinWearing : ""}
                    </Text>
                  )}
                  {(cols.includes("Executive Committee") ||
                    cols.length < 1) && (
                    <Text style={[styles.light, { width: "10%" }]}>
                      {currentInfo && currentInfo.isExecutiveCommittee
                        ? "Yes"
                        : "No"}
                    </Text>
                  )}
                  {(cols.includes("Voting Member") || cols.length < 1) && (
                    <Text style={[styles.light, { width: "10%" }]}>
                      {currentInfo && currentInfo.isVotingMember ? "Yes" : "No"}
                    </Text>
                  )}
                  {(cols.includes("Capital Share") || cols.length < 1) && (
                    <Text style={[styles.light, { width: "10%" }]}>
                      {currentInfo ? currentInfo.capitalShare : ""}
                    </Text>
                  )}
                  {(cols.includes("Year Qualified") || cols.length < 1) && (
                    <Text style={[styles.light, { width: "10%" }]}>
                      {diamond?.businessInfo?.yearDiamondQualified
                        ? diamond.businessInfo.yearDiamondQualified
                        : ""}
                    </Text>
                  )}
                </View>
              );
            })}
          </View>
          <View style={styles.footer} fixed>
            <Text>
              {`This document is confidential and subject to attorney client privilege. For IBO Use Only. | Revised ${todaysDate} | CR# Not Reviewed`}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}
